<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AppointmentsAPI from 'dashboard/api/appointments';
import StatusBadge from 'dashboard/components-next/appointments/StatusBadge.vue';

// Read-only passive widget: lists appointments linked to the current conversation
// (via conversation_display_id) so agents see booking context without leaving the
// conversation. fase-29.
const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t, locale } = useI18n();
const appointments = ref([]);
const loading = ref(false);

function formatDateTime(iso) {
  return new Intl.DateTimeFormat((locale.value || 'en-US').replace('_', '-'), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

async function load() {
  if (!props.conversationId) {
    appointments.value = [];
    return;
  }
  loading.value = true;
  try {
    const response = await AppointmentsAPI.list({
      conversation_display_id: props.conversationId,
    });
    appointments.value = (response.data?.data || [])
      .slice()
      .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  } catch {
    appointments.value = [];
  } finally {
    loading.value = false;
  }
}

onMounted(load);
watch(() => props.conversationId, load);
</script>

<template>
  <div class="px-1">
    <div v-if="loading" class="h-10 bg-n-alpha-1 rounded-lg animate-pulse" />
    <p v-else-if="!appointments.length" class="text-sm text-n-slate-11 py-2">
      {{ t('APPOINTMENTS.CONVERSATION_WIDGET.EMPTY') }}
    </p>
    <div v-else class="space-y-1.5">
      <div
        v-for="appt in appointments"
        :key="appt.id"
        class="rounded-lg border border-n-weak bg-n-background p-2.5 text-sm"
      >
        <div class="flex items-center justify-between gap-2 mb-1">
          <StatusBadge :status="appt.status" />
          <span class="text-[11px] text-n-slate-10">
            {{ formatDateTime(appt.scheduled_at) }}
          </span>
        </div>
        <p class="font-semibold text-n-slate-12 truncate">
          {{ appt.service?.name }}
        </p>
        <p
          v-if="appt.professional"
          class="text-[11px] text-n-slate-10 truncate"
        >
          {{ appt.professional.name }}
        </p>
      </div>
    </div>
  </div>
</template>
